import React, {Fragment} from "react";
import PropTypes from "prop-types";

export default class FuxOffsetPaginator extends React.Component {
    constructor(props) {
        super(props);
        this.initialState = {
            data: [],
            currentPage: 1,
            totalItems: 0,
            itemsPerPage: 1,
            totalPages: 1,
            prevPage: null,
            nextPage: null,
            fetching: false
        };
        this.state = {...this.initialState};

        if (this.props.refreshTime && this.props.refreshTime > 0) {
            setInterval(() => {
                this.fetch(this.state.currentPage, true);
            }, this.props.refreshTime);
        }
    }

    componentDidMount() {
        this.fetch(1);
    }

    normalizePagination = page => {
        const items = page && page.data ? page.data : [];
        const totalItems = page && page.total ? parseInt(page.total, 10) : 0;
        const itemsPerPage = page && page.max_items ? parseInt(page.max_items, 10) : 1;
        const totalPages = page && page.pages ? parseInt(page.pages, 10) : 1;
        const prevPage = page && page.prev ? parseInt(page.prev, 10) : null;
        const nextPage = page && page.next ? parseInt(page.next, 10) : null;

        return {
            items: items,
            totalItems: Number.isNaN(totalItems) ? 0 : totalItems,
            itemsPerPage: Number.isNaN(itemsPerPage) ? 1 : itemsPerPage,
            totalPages: Number.isNaN(totalPages) ? 1 : totalPages,
            prevPage: Number.isNaN(prevPage) ? null : prevPage,
            nextPage: Number.isNaN(nextPage) ? null : nextPage
        };
    }

    fetch = (page, silent = false) => {
        if (!silent) {
            this.setState({fetching: true});
        }
        this.props.onPageRequest(page)
            .then(pageData => {
                const normalized = this.normalizePagination(pageData || {});
                this.setState({
                    data: normalized.items,
                    totalItems: normalized.totalItems,
                    itemsPerPage: normalized.itemsPerPage,
                    currentPage: page,
                    totalPages: normalized.totalPages,
                    prevPage: normalized.prevPage,
                    nextPage: normalized.nextPage,
                    fetching: false
                });
            });
    }

    reset = _ => this.setState({...this.initialState}, _ => this.fetch(1));

    handleGoPrev = _ => {
        if (!this.state.prevPage) return;
        this.fetch(this.state.prevPage);
    }

    handleGoNext = _ => {
        if (!this.state.nextPage) return;
        this.fetch(this.state.nextPage);
    }

    handleGoToPage = page => _ => {
        if (page === this.state.currentPage) return;
        this.fetch(page);
    }

    getVisiblePages = () => {
        const totalPages = this.state.totalPages || 1;
        const maxVisible = this.props.visiblePagesCount || 5;
        if (totalPages <= maxVisible) {
            return [...Array(totalPages)].map((_, i) => i + 1);
        }

        const half = Math.floor(maxVisible / 2);
        let start = this.state.currentPage - half;
        let end = this.state.currentPage + half;

        if (maxVisible % 2 === 0) {
            end -= 1;
        }

        if (start < 1) {
            start = 1;
            end = maxVisible;
        }

        if (end > totalPages) {
            end = totalPages;
            start = totalPages - maxVisible + 1;
        }

        const pages = [];
        for (let i = start; i <= end; i += 1) {
            pages.push(i);
        }
        return pages;
    }

    render() {
        if (this.state.fetching) {
            if (!this.props.onItemSkeletonRender || !this.props.skeletonItemsNum) {
                return (
                    <div className={"w-100 text-center"}>
                        <i className="fa fa-spin fa-spinner"></i> Ricerca in corso...
                    </div>
                );
            }
        }

        if (!this.state.data.length) {
            return (this.props.onEmptyPage());
        }

        const visiblePages = this.getVisiblePages();

        const isSinglePage = this.state.totalPages === 1;

        return (
            <div>
                <div className={this.props.classItemsContainer || ""}>
                    {
                        this.state.fetching ?
                            [...Array(this.props.skeletonItemsNum || 3)].map((_, i) => {
                                return <Fragment key={i}>{this.props.onItemSkeletonRender(i)}</Fragment>;
                            })
                            :
                            this.state.data.map((item, k) => {
                                return <Fragment key={k}>{this.props.onItemRender(item, k, this.state.currentPage)}</Fragment>;
                            })
                    }
                </div>

                {
                    (!this.props.hideControls && !isSinglePage) &&
                    <div className="d-flex justify-content-center mt-3">
                        <nav className="text-center">
                            <ul className="pagination">
                                <li className="page-item">
                                    <button
                                        className="page-link"
                                        data-role="prev"
                                        disabled={!this.state.prevPage}
                                        onClick={this.handleGoPrev}
                                    >
                                        Precedente
                                    </button>
                                </li>
                                {
                                    visiblePages.map(page => {
                                        return (
                                            <li
                                                className={"page-item " + (page === this.state.currentPage ? "active" : "")}
                                                key={page}
                                            >
                                                <button
                                                    className="page-link"
                                                    data-role={"page-" + page}
                                                    onClick={this.handleGoToPage(page)}
                                                >
                                                    {page}
                                                </button>
                                            </li>
                                        );
                                    })
                                }
                                <li className="page-item">
                                    <button
                                        className="page-link"
                                        data-role="next"
                                        disabled={!this.state.nextPage}
                                        onClick={this.handleGoNext}
                                    >
                                        Successivo
                                    </button>
                                </li>
                            </ul>
                            <span data-role="label">
                                Pagina {this.state.currentPage} di {this.state.totalPages}
                            </span>
                        </nav>
                    </div>
                }
            </div>
        );
    }
}

FuxOffsetPaginator.propTypes = {
    onItemRender: PropTypes.func,
    onItemSkeletonRender: PropTypes.func,
    skeletonItemsNum: PropTypes.number,
    onPageRequest: PropTypes.func,
    onEmptyPage: PropTypes.func,
    refreshTime: PropTypes.number,
    hideControls: PropTypes.bool,
    classItemsContainer: PropTypes.string,
    visiblePagesCount: PropTypes.number
};

FuxOffsetPaginator.defaultProps = {
    skeletonItemsNum: 3,
    visiblePagesCount: 5
};
